import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

/**
 * Fetch the aggregate payload behind the Portfolio Story page.
 *
 * One request to `GET /api/story` covers all nine chapters — the page sits on
 * the login-critical path, so it does not fan out across six endpoints.
 *
 * The page renders its structure immediately and fills in values when this
 * resolves, so `story` is null while loading rather than a skeleton shape.
 *
 * @returns {{story: Object|null, loading: boolean, error: string|null}}
 */
export const useStory = () => {
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchStory = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(`${API_BASE_URL}/story`, {
          withCredentials: true,
        });

        if (!cancelled) {
          setStory(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Could not load your story');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchStory();

    return () => {
      cancelled = true;
    };
  }, []);

  return { story, loading, error };
};

export default useStory;
